import { DataGrid } from './DataGrid';
import { clamp } from './Utils';

export interface PageIndexes {
  startColIdx: number;
  endColIdx: number;
  startRowIdx: number;
  endRowIdx: number;
}

/**
 * Used for storing column and row ranges when calculating page indexes.
 */
interface Range {
  start: number,
  end: number;
}

/**
 * Our paging strategy is to determine the exact (or estimated) column and row index ranges that will fit into the ViewPort. Once we determine this information we:
 *
 *    1) Normalize the exact indexes against section boundaries
 *    2) For each index a buffer section is added if the exact index is "too close" to the normalized index
 *
 * For example, let's say that we have the following...
 *
 *  - The exact first and last column indexes are determined to be 14 and 29
 *  - The amount of columns per paged section ('columnsPerPagedSection') is 10 (i.e. inclusive columns 0 - 9, 10 - 19, 20 - 29, 30 - 39, etc.)
 *  - The exact first and last row indexes are determined to be 22 and 50
 *  - The amount of rows per paged section ('rowsPerPagedSection') is 20 (i.e inclusive rows 0 - 19, 20 - 39, 40 - 59, etc.)
 *
 *    1) We first normalize the exact indexes against section boundaries:
 *
 *      - The normalized first and last column indexes become 10 and 29
 *      - The normalized first and last row indexes become 20 and 59
 *
 *    2) We then add a buffer section if the exact index is "too close" to the normalized index
 *
 *      - The threshold for columns is 'sectionBoundaryThreshold' * 'columnsPerPagedSection': 0.3 * 10 = 3 columns
 *      - The exact first column index (14) is not within the threshold (3) columns of the normalized first column (10) - no section buffer is added
 *      - The exact last column index (29) is within the threshold (3) columns of the normalized last column (29) - a section buffer is added and now our normalized last column becomes 39
 *
 *      - The threshold for rows is 'sectionBoundaryThreshold' * 'rowsPerPagedSection': 0.3 * 20 = 6 rows
 *      - The exact first row index (22) is within the threshold (6) rows of the normalized first row (20) - a section buffer is added and now our normalized first row becomes 0
 *      - The exact last row index (50) is not within the threshold (6) rows of the normalized last row (59) - no section buffer is added
 *
 *    The resulting pageIndexes are:
 *
 *      startColIdx: 10
 *      endColIdx: 39
 *      startRowIdx: 0
 *      endRowIdx: 59
 */
export class Paging {
  /**
   * A percentage of the 'columnsPerPagedSection' and 'rowsPerPagedSection' - used as a threshold for determining if exact indexes are
   * too close to their corresponding normalized indexes.
   */
  private static readonly sectionBoundaryThreshold: number = 0.3;

  /**
   * Gets the column and row indexes (normalized against column and row section boundaries) for the exact indexes that are displayed
   * within the viewport.
   */
  public static getSectionedPageIndexesForScrollPosition(
    scrollLeft: number,
    scrollTop: number,
    rowCount: number,
    columns: DataGrid.Column[],
    columnWidths: DataGrid.ColumnWidths,
    minimumColumnWidth: number,
    bodyRect: ClientRect,
    rowOuterHeight: number,
    columnsPerPagedSection: number,
    rowsPerPagedSection: number
  ): PageIndexes {
    return Paging.convertExactIndexesToSectionedIndexes(
      Paging.getExactPageIndexesForScrollPosition(scrollLeft, scrollTop, rowCount, columns, columnWidths, minimumColumnWidth, bodyRect, rowOuterHeight),
      columnsPerPagedSection,
      rowsPerPagedSection,
      columns.length,
      rowCount
    );
  }

  /**
   * Gets the column and row indexes (normalized against column and row section boundaries) for the passed cell. The passed cell will be
   * centered within the normalized column and row indexes.
   */
  public static getSectionedPageIndexesForCell(
    colIdx: number,
    rowIdx: number,
    rowCount: number,
    columns: DataGrid.Column[],
    columnWidths: DataGrid.ColumnWidths,
    minimumColumnWidth: number,
    bodyRect: ClientRect,
    rowOuterHeight: number,
    columnsPerPagedSection: number,
    rowsPerPagedSection: number
  ): PageIndexes {
    const columnRange = this.getEstimatedColumnRangeForCell(
      colIdx,
      columns,
      columnWidths,
      minimumColumnWidth,
      bodyRect.width
    );

    const rowRange = this.getEstimatedRowRangeForCell(
      rowIdx,
      rowCount,
      bodyRect.height,
      rowOuterHeight
    );

    const exactIndexes = {
      startColIdx: columnRange.start,
      endColIdx: columnRange.end,
      startRowIdx: rowRange.start,
      endRowIdx: rowRange.end
    } as PageIndexes;

    return Paging.convertExactIndexesToSectionedIndexes(
      exactIndexes,
      columnsPerPagedSection,
      rowsPerPagedSection,
      columns.length,
      rowCount
    );
  }

  /**
   * Returns true iff the specified cell is within the passed pageIndexes
   */
  public static isCellPaged(colIdx: number, rowIdx: number, pageIndexes: PageIndexes): boolean {
    return colIdx >= pageIndexes.startColIdx && colIdx <= pageIndexes.endColIdx && rowIdx >= pageIndexes.startRowIdx && rowIdx <= pageIndexes.endRowIdx;
  }

  /**
   * Gets the exact column and row indexes that are displayed within the viewport.
   */
  private static getExactPageIndexesForScrollPosition(
    scrollLeft: number,
    scrollTop: number,
    rowCount: number,
    columns: DataGrid.Column[],
    columnWidths: DataGrid.ColumnWidths,
    minimumColumnWidth: number,
    bodyRect: ClientRect,
    rowOuterHeight: number
  ): PageIndexes {
    const lastColumnIndex = Math.max(columns.length - 1, 0);
    const lastRowIndex = Math.max(rowCount - 1, 0);

    rowOuterHeight = Math.max(rowOuterHeight, 1);
    minimumColumnWidth = Math.max(minimumColumnWidth, 1);

    const viewportTop = Math.max(scrollTop, 0);
    const viewportBottom = Math.max(viewportTop + bodyRect.height, 0);

    let viewportLeft = Math.max(scrollLeft, 0);
    let viewportRight = Math.max(viewportLeft + bodyRect.width, 0);

    let startColIdx: number | undefined = undefined;
    let endColIdx: number | undefined = undefined;

    for (let colIdx = 0; colIdx < lastColumnIndex; colIdx++) {
      const columnWidth = columnWidths[columns[colIdx].name] || minimumColumnWidth;
      viewportLeft -= columnWidth;
      viewportRight -= columnWidth;

      if (startColIdx === undefined && viewportLeft <= 0) {
        startColIdx = colIdx;
      }

      if (endColIdx === undefined && (viewportRight <= 0 || colIdx  === lastColumnIndex)) {
        endColIdx = colIdx;
        break;
      }
    }

    if (startColIdx === undefined || endColIdx === undefined) {
      startColIdx = lastColumnIndex - Math.floor(bodyRect.width / minimumColumnWidth);
      endColIdx = lastColumnIndex;
    }

    let startRowIdx = Math.floor(viewportTop / rowOuterHeight);
    let endRowIdx = Math.ceil(viewportBottom / rowOuterHeight);
    if (endRowIdx > lastRowIndex) {
      const excessRows = endRowIdx - lastRowIndex;
      startRowIdx -= excessRows;
      endRowIdx = lastRowIndex;
    }

    return {
      startColIdx: clamp(startColIdx || 0, 0, lastColumnIndex),
      endColIdx: clamp(endColIdx || 0, 0, lastColumnIndex),
      startRowIdx: clamp(startRowIdx || 0, 0, lastRowIndex),
      endRowIdx: clamp(endRowIdx || 0, 0, lastRowIndex)
    } as PageIndexes;
  }

  /**
   * Gets the estimated column start and end indexes that fill the ViewPort width with the passed cell being centered (if possible).
   * If the passed cell will not be centered within the ViewPort width (due to being too close to the min/max for the amount of
   * columns), then we add buffer before/after to ensure that the total amount of columns will fill the ViewPort width.
   */
  private static getEstimatedColumnRangeForCell(
    colIdx: number,
    columns: DataGrid.Column[],
    columnWidths: DataGrid.ColumnWidths,
    minimumColumnWidth: number,
    bodyRectWidth: number
  ): Range {
    const lastColumnIndex = Math.max(columns.length - 1, 0);

    if (colIdx < 0) {
      colIdx = 0;
    } else if (colIdx > lastColumnIndex) {
      colIdx = lastColumnIndex;
    }

    const columnRange: Range = {
      start: colIdx,
      end: colIdx
    };

    while (bodyRectWidth > 0 && !(columnRange.end === lastColumnIndex && columnRange.start === 0)) {
      if (columnRange.start === colIdx && columnRange.end === colIdx) {
        bodyRectWidth -= columnWidths[columns[colIdx].name] || minimumColumnWidth;
      }

      if (columnRange.start > 0) {
        columnRange.start -= 1;
        bodyRectWidth -= columnWidths[columns[columnRange.start].name] || minimumColumnWidth;
      } else if (columnRange.end < lastColumnIndex) {
        columnRange.end += 1;
        bodyRectWidth -= columnWidths[columns[columnRange.end].name] || minimumColumnWidth;
      }

      if (columnRange.end < lastColumnIndex) {
        columnRange.end += 1;
        bodyRectWidth -= columnWidths[columns[columnRange.end].name] || minimumColumnWidth;
      } else if (columnRange.start > 0) {
        columnRange.start -= 1;
        bodyRectWidth -= columnWidths[columns[columnRange.start].name] || minimumColumnWidth;
      }
    }

    return columnRange;
  }

  /**
   * Gets the estimated row start and end indexes that fill the ViewPort height with the passed cell being centered (if possible).
   * If the passed cell will not be centered within the ViewPort height (due to being too close to the min/max for the amount of
   * rows), then we add buffer before/after to ensure that the total amount of rows will fill the ViewPort height.
   */
  private static getEstimatedRowRangeForCell(
    rowIdx: number,
    rowCount: number,
    bodyRectHeight: number,
    rowOuterHeight: number
  ): Range {
    const lastRowIndex = Math.max(rowCount - 1, 0);

    if (rowIdx < 0) {
      rowIdx = 0;
    } else if (rowIdx > lastRowIndex) {
      rowIdx = lastRowIndex;
    }

    const rowsInViewPort = Math.ceil(bodyRectHeight / rowOuterHeight) + 1;
    const rowBuffer = Math.ceil(rowsInViewPort / 2);

    const rowRange: Range = {
      start: Math.max(rowIdx - rowBuffer, 0),
      end: Math.min(rowIdx + rowBuffer, lastRowIndex)
    };

    const rowRangeDelta = rowRange.end - rowRange.start;

    if (rowRange.start === 0 && rowRangeDelta < rowsInViewPort) {
      rowRange.end = clamp(rowRange.end + (rowsInViewPort - rowRangeDelta), 0, lastRowIndex);
    } else if (rowRange.end === lastRowIndex && rowRangeDelta < rowsInViewPort) {
      rowRange.start = clamp(rowRange.start - (rowsInViewPort - rowRangeDelta), 0, lastRowIndex);
    }

    return rowRange;
  }

  /**
   * Converts the passed indexes into sectioned indexes (normalized against column and row section boundaries).
   */
  private static convertExactIndexesToSectionedIndexes(
    exactIndexes: PageIndexes,
    columnsPerPagedSection: number,
    rowsPerPagedSection: number,
    columnCount: number,
    rowCount: number
  ): PageIndexes {
    const lastColumnIndex = Math.max(columnCount - 1, 0);
    const lastRowIndex = Math.max(rowCount - 1, 0);

    // Add buffer section(s), if necessary, to the column start and end sections
    const columnThreshold = Math.ceil(Paging.sectionBoundaryThreshold * columnsPerPagedSection);

    let startColSection = Math.floor(exactIndexes.startColIdx / columnsPerPagedSection);
    if (startColSection > 0 && exactIndexes.startColIdx % columnsPerPagedSection <= columnThreshold) {
      startColSection--;
    }

    let endColSection = Math.ceil(exactIndexes.endColIdx / columnsPerPagedSection);
    const columnsInLastSection = exactIndexes.endColIdx % columnsPerPagedSection;
    if (columnsInLastSection === 0 || columnsPerPagedSection - columnsInLastSection <= columnThreshold) {
      endColSection++;
    }

    // Add buffer section(s), if necessary, to the row start and end sections
    const rowThreshold = Math.ceil(Paging.sectionBoundaryThreshold * rowsPerPagedSection);

    let startRowSection = Math.floor(exactIndexes.startRowIdx / rowsPerPagedSection);
    if (startRowSection > 0 && exactIndexes.startRowIdx % rowsPerPagedSection <= rowThreshold) {
      startRowSection--;
    }

    let endRowSection = Math.ceil(exactIndexes.endRowIdx / rowsPerPagedSection);
    const rowsInLastSection = exactIndexes.endRowIdx % rowsPerPagedSection;
    if (rowsInLastSection === 0 || rowsPerPagedSection - rowsInLastSection <= rowThreshold) {
      endRowSection++;
    }

    return {
      startColIdx: clamp(startColSection * columnsPerPagedSection, 0, lastColumnIndex),
      endColIdx: clamp(endColSection * columnsPerPagedSection - 1, 0, lastColumnIndex),
      startRowIdx: clamp(startRowSection * rowsPerPagedSection, 0, lastRowIndex),
      endRowIdx: clamp(endRowSection * rowsPerPagedSection - 1, 0, lastRowIndex)
    } as PageIndexes;
  }
}
